import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from 'v-select2-multiple-component';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";

export default {
  name: "courseImport",
  components: {
    MainLayout,
    Select2Multiple,
    flatPickr,
    Loading,
  },
  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      acdYearList: [],
      templateAcademicYearId: null,
      targetAcademicYearId: null,
      file_name: '',
      invalidFileType: false,
      invalidFileSize: false,
      submitted: false,
      templateSubmitted: false,
      disableImportBtn: false,
      showPreview: false,
      previewData: [],
      previewSummary: null,
    };
  },

  validations: {
    targetAcademicYearId: { required },
    file_name: { required },
  },

  computed: {
    disabledTemplateBtn() {
      return this.previewData && this.previewData.length > 0;
    },

    selectedAcademicYearName() {
      const selected = this.acdYearList.find(
        (period) => String(period.id) === String(this.targetAcademicYearId)
      );

      return selected?.text || this.previewData[0]?.academic_year_name || "";
    },
  },

  methods: {
    async getAcademicYearList() {
      try {
        this.isLoading = true;
        const response = await axios.get("period/periodList");
        if (response.data) {
          this.acdYearList = response.data.data || [];
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async templateDownload() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      this.isLoading = true;

      try {
        const campusId = this.$store.state.user.campus_id;
        const params = { campus_id: campusId };
        if (this.templateAcademicYearId) {
          params.academic_year_id = this.templateAcademicYearId;
        }
        const response = await axios({
          method: 'get',
          url: '/course/export',
          params,
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const selectedYear = this.acdYearList.find(y => y.id == this.templateAcademicYearId);
        const now = new Date();
        const dateStr = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}`;
        const periodName = (selectedYear?.text || 'all').replace(/\s+/g, '_');
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${periodName}_courses_${dateStr}.xlsx`;
        link.click();
      } catch (error) {
        if (error.response && error.response.status === 500) {
          try {
            const text = await error.response.data.text();
            const data = JSON.parse(text);
            alert(data.message || "Failed to download template.");
          } catch (e) {
            alert("Failed to download template.");
          }
        } else {
          alert("Failed to download template.");
        }
      } finally {
        this.isLoading = false;
      }
    },

    chooseFile() {
      this.$refs.fileUploader.value = null;
      this.file_name = '';
      this.showPreview = false;
      this.previewData = [];
      this.previewSummary = null;
      this.clearImportConfirmErrors();
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      this.showPreview = false;
      this.previewData = [];
      this.previewSummary = null;
      this.clearImportConfirmErrors();
      const file = event.target.files[0];
      if (!file) return;

      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'xlsx') {
        this.invalidFileType = true;
        this.file_name = '';
        return;
      }
      this.invalidFileType = false;
      this.file_name = file.name;
    },

    async confirmImport() {
      this.submitted = true;
      if (this.$v.$invalid) return;
      this.validationErrors = {};
      this.clearValidationErrors();

      const file = this.$refs.fileUploader.files[0];
      if (!file) {
        this.invalidFileType = true;
        this.file_name = '';
        return;
      }
      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        this.invalidFileSize = true;
        return;
      }
      this.invalidFileSize = false;

      await this.doValidate();
    },

    async doValidate() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.validationErrors = {};

      const formData = new FormData();
      formData.append('target_academic_year_id', this.targetAcademicYearId);
      formData.append('campus_id', this.$store.state.user.campus_id);
      formData.append('file', this.$refs.fileUploader.files[0]);

      try {
        const response = await axios.post('/course/import/validate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data && response.data.success && response.data.preview) {
          this.previewData = this.clonePreviewData(response.data.preview.courses || []);
          this.previewSummary = response.data.preview.summary || null;
          this.clearImportConfirmErrors();
          this.showPreview = true;
        }
      } catch (error) {
        if (!error.response) {
          alert("Network error. Please try again.");
          this.file_name = '';
          this.$refs.fileUploader.value = null;
          this.submitted = false;
        } else if (error.response.status === 422) {
          const data = error.response.data;
          let errorMessages = [];

          if (Array.isArray(data.error_messages) && data.error_messages.length) {
            errorMessages = data.error_messages;
          } else if (data.errors && typeof data.errors === 'object') {
            errorMessages = [].concat(...Object.values(data.errors));
          }

          if (errorMessages.length) {
            alert(errorMessages.join("\n"));
          } else if (data.msg) {
            alert(data.msg);
          } else if (data.message) {
            alert(data.message);
          } else {
            this.printValidationError(error.response);
          }
        } else if (error.response.status === 401) {
          this.handleError(error);
        } else {
          alert("Something went wrong. Please try again.");
        }
      } finally {
        this.disableImportBtn = false;
        this.isLoading = false;
      }
    },

    async doImport() {
      this.clearImportConfirmErrors();

      if (!this.previewData.length) {
        alert("No data to import.");
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableImportBtn = true;

      try {
        const response = await axios.post('/course/import', {
          target_academic_year_id: this.targetAcademicYearId,
          campus_id: this.$store.state.user.campus_id,
          created_id: this.$store.state.user.id,
          courses: JSON.stringify(this.buildConfirmPreviewPayload()),
        });

        if (response.data && response.data.success) {
          this.$notification.success(response.data.message || "Import completed successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.showList();
        }
      } catch (error) {
        this.handleConfirmBackendError(error);
      } finally {
        this.disableImportBtn = false;
        this.isLoading = false;
      }
    },

    clonePreviewData(rows) {
      return (rows || []).map((row) => ({
        row_num: row.row_num,
        class_id: row.class_id,
        class_name: String(row.class_name || "").trim(),
        teacher_id: row.teacher_id,
        teacher_name: String(row.teacher_name || "").trim(),
        teacher_no: String(row.teacher_no || "").trim(),
        subject_id: row.subject_id,
        subject_name: String(row.subject_name || "").trim(),
        unit: String(row.unit || "").trim(),
        lesson: String(row.lesson || "").trim(),
        send_date: String(row.send_date || "").trim(),
        file_url: String(row.file_url || "").trim(),
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewData.map((row) => ({
        row_num: row.row_num,
        class_name: String(row.class_name || "").trim(),
        teacher_name: String(row.teacher_name || "").trim(),
        teacher_no: String(row.teacher_no || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        unit: String(row.unit || "").trim(),
        lesson: String(row.lesson || "").trim(),
        send_date: String(row.send_date || "").trim(),
        file_url: String(row.file_url || "").trim(),
      }));
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const classNamePattern = /^(.+?)\s*\((.+?)\)\s*$/;

      this.previewData.forEach((row, index) => {
        const rowErrors = {};
        const className = String(row.class_name || "").trim();
        const teacherName = String(row.teacher_name || "").trim();
        const teacherNo = String(row.teacher_no || "").trim();
        const subjectName = String(row.subject_name || "").trim();
        const unit = String(row.unit || "").trim();
        const lesson = String(row.lesson || "").trim();
        const sendDate = String(row.send_date || "").trim();

        if (!className) {
          rowErrors.class_name = "Class Name is required.";
        } else if (!classNamePattern.test(className)) {
          rowErrors.class_name = "Expected 'GradeName (Section)'.";
        }

        if (!teacherName) {
          rowErrors.teacher_name = "Teacher Name is required.";
        }

        if (!teacherNo) {
          rowErrors.teacher_no = "Teacher No is required.";
        }

        if (!subjectName) {
          rowErrors.subject_name = "Subject Name is required.";
        }

        if (!unit) {
          rowErrors.unit = "Unit is required.";
        }

        if (!lesson) {
          rowErrors.lesson = "Lesson is required.";
        }

        if (!sendDate) {
          rowErrors.send_date = "Send Date is required.";
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(sendDate)) {
          rowErrors.send_date = "Must be YYYY-MM-DD.";
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    handleConfirmBackendError(error) {
      if (!error.response) {
        alert("Network error. Please try again.");
        return;
      }

      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }

      if (error.response.status === 422) {
        const data = error.response.data || {};
        const messages = Array.isArray(data.errors) && data.errors.length
          ? data.errors
          : [data.msg || data.message || "Import validation failed."];
        alert(messages.join("\n"));
        return;
      }

      if (error.response.status === 500) {
        alert("Import failed. All changes rolled back.");
        return;
      }

      alert(error.response.data?.message || "Something went wrong.");
    },

    getImportConfirmErrorOptions() {
      return {
        rowKey: "row_num",
        payloadPrefixes: ["courses"],
      };
    },

    resetAll() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.templateAcademicYearId = null;
      this.targetAcademicYearId = null;
      this.file_name = '';
      this.invalidFileType = false;
      this.invalidFileSize = false;
      this.showPreview = false;
      this.previewData = [];
      this.previewSummary = null;
      this.clearImportConfirmErrors();
      this.validationErrors = {};
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYearList();
    },

    showList() {
      this.$router.push({ path: '/course/list' });
    },

    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$store.commit('logout');
        window.location.replace('login');
      } else {
        console.error(error);
      }
    },
  },

  mounted() {
    this.getAcademicYearList();
  },
};
